import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/Card/Card';
import Button from '../../components/Button/Button';
import Loading from '../../components/Loading/Loading';
import Modal from '../../components/Modal/Modal';
import api from '../../services/api';
import { EventWithShortCode, InviteWithGuests } from '../../types';
import { formatDateRange, getErrorMessage } from '../../utils/helpers';
import {
  buildGuestCsvFilename,
  buildGuestCsvRows,
  createGuestCsv,
  downloadGuestCsv,
} from './guestCsv';
import './EventDetail.scss';

const EventDetail: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState<EventWithShortCode | null>(null);
  const [invites, setInvites] = useState<InviteWithGuests[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expandedInvites, setExpandedInvites] = useState<Set<string>>(new Set());
  
  // Filtering and sorting
  const [sortBy, setSortBy] = useState<'title' | 'email' | 'dietary' | 'respondedAt'>('title');
  const [filterRsvp, setFilterRsvp] = useState<'all' | 'yes' | 'no' | 'pending' | 'responded'>('all');
  const [filterDietary, setFilterDietary] = useState<'all' | 'has' | 'none'>('all');
  const [filterOtherInfo, setFilterOtherInfo] = useState<'all' | 'has' | 'none'>('all');
  const [filterEmail, setFilterEmail] = useState<'all' | 'has' | 'none'>('all');

  const fetchEventDetails = useCallback(async () => {
    if (!user?.uuid || !eventId) return;

    try {
      setLoading(true);
      const eventData = await api.getEvent(user.uuid, eventId);
      const invitesData = await api.listInvites(user.uuid, eventId);
      
      // Fetch guests for each invite
      const invitesWithGuests = await Promise.all(
        invitesData.map(async (invite) => {
          const guests = await api.listGuests(user.uuid, eventId, invite.uuid);
          return { ...invite, guests };
        })
      );
      
      setEvent(eventData);
      setInvites(invitesWithGuests);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [user, eventId]);

  useEffect(() => {
    fetchEventDetails();
  }, [fetchEventDetails]);

  const handleDelete = async () => {
    if (!user?.uuid || !eventId) return;

    try {
      setDeleting(true);
      await api.deleteEvent(user.uuid, eventId);
      navigate('/events');
    } catch (err) {
      setError(getErrorMessage(err));
      setDeleting(false);
    }
  };

  const handleDeleteInvite = async (inviteId: string) => {
    if (!user?.uuid || !eventId) return;

    try {
      await api.deleteInvite(user.uuid, eventId, inviteId);
      setInvites(invites.filter((inv) => inv.uuid !== inviteId));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleDeleteGuest = async (inviteId: string, guestId: string) => {
    if (!user?.uuid || !eventId) return;

    try {
      await api.deleteGuest(user.uuid, eventId, inviteId, guestId);
      setInvites(invites.map(inv => {
        if (inv.uuid === inviteId) {
          return { ...inv, guests: inv.guests.filter(g => g.uuid !== guestId) };
        }
        return inv;
      }));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const toggleInviteExpanded = (inviteId: string) => {
    setExpandedInvites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(inviteId)) {
        newSet.delete(inviteId);
      } else {
        newSet.add(inviteId);
      }
      return newSet;
    });
  };

  const toggleAllInvitesExpanded = () => {
    if (expandedInvites.size === invites.length) {
      setExpandedInvites(new Set());
    } else {
      setExpandedInvites(new Set(invites.map(inv => inv.uuid)));
    }
  };

  // Filter and sort invites
  const filteredAndSortedInvites = React.useMemo(() => {
    let filtered = [...invites];

    // Apply email filter
    if (filterEmail !== 'all') {
      filtered = filtered.filter((inv) => {
        if (filterEmail === 'has') return inv.email && inv.email.trim() !== '';
        if (filterEmail === 'none') return !inv.email || inv.email.trim() === '';
        return true;
      });
    }

    // Apply dietary filter
    if (filterDietary !== 'all') {
      filtered = filtered.filter((inv) => {
        if (filterDietary === 'has') return inv.dietary && inv.dietary.trim() !== '';
        if (filterDietary === 'none') return !inv.dietary || inv.dietary.trim() === '';
        return true;
      });
    }

    // Apply other info filter
    if (filterOtherInfo !== 'all') {
      filtered = filtered.filter((inv) => {
        if (filterOtherInfo === 'has') return inv.otherInfo && inv.otherInfo.trim() !== '';
        if (filterOtherInfo === 'none') return !inv.otherInfo || inv.otherInfo.trim() === '';
        return true;
      });
    }

    // Apply RSVP filter (based on guests)
    if (filterRsvp !== 'all') {
      filtered = filtered.filter((inv) => {
        if (filterRsvp === 'yes') return inv.guests.some(g => g.rsvp === true);
        if (filterRsvp === 'no') return inv.guests.some(g => g.rsvp === false);
        if (filterRsvp === 'pending') return inv.guests.some(g => g.rsvp === null || g.rsvp === undefined);
        if (filterRsvp === 'responded') return inv.guests.some(g => g.rsvp !== null && g.rsvp !== undefined);
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      } else if (sortBy === 'email') {
        const aEmail = a.email || '';
        const bEmail = b.email || '';
        return aEmail.localeCompare(bEmail);
      } else if (sortBy === 'dietary') {
        const aDietary = a.dietary || '';
        const bDietary = b.dietary || '';
        return aDietary.localeCompare(bDietary);
      } else if (sortBy === 'respondedAt') {
        if (a.rsvpSubmittedAt === null) return b.rsvpSubmittedAt === null ? 0 : 1;
        if (b.rsvpSubmittedAt === null) return -1;
        return new Date(b.rsvpSubmittedAt).getTime() - new Date(a.rsvpSubmittedAt).getTime();
      }
      return 0;
    });

    return filtered;
  }, [invites, sortBy, filterRsvp, filterDietary, filterOtherInfo, filterEmail]);

  const guestCsvRows = React.useMemo(
    () => buildGuestCsvRows(filteredAndSortedInvites, filterRsvp),
    [filteredAndSortedInvites, filterRsvp]
  );

  const handleExportGuests = () => {
    if (!event || guestCsvRows.length === 0) return;

    const filename = buildGuestCsvFilename(event.title, {
      email: filterEmail,
      rsvp: filterRsvp,
      dietary: filterDietary,
      otherInfo: filterOtherInfo,
    });

    downloadGuestCsv(createGuestCsv(guestCsvRows), filename);
  };

  // Calculate stats
  const totalGuests = invites.reduce((sum, inv) => sum + inv.guests.length, 0);
  const rsvpYes = invites.reduce((sum, inv) => 
    sum + inv.guests.filter(g => g.rsvp === true).length, 0
  );
  const rsvpNo = invites.reduce((sum, inv) => 
    sum + inv.guests.filter(g => g.rsvp === false).length, 0
  );
  const noResponse = invites.reduce((sum, inv) => 
    sum + inv.guests.filter(g => g.rsvp === null || g.rsvp === undefined).length, 0
  );

  if (loading) {
    return <Loading message="Loading event details..." />;
  }

  if (!event) {
    return (
      <div className="container">
        <Card>
          <p className="text-center">Event not found</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="event-detail">
      <div className="container">
        {error && <div className="error-message">{error}</div>}

        <Card>
          <div className="event-detail__header">
            <div>
              <h1>{event.title}</h1>
              <p className="event-date">{formatDateRange(event.startDate, event.endDate)}</p>
              {event.description && <p className="mt-md">{event.description}</p>}
              <div className="event-shortcode mt-md">
                Event Code: <strong>{event.shortcode}</strong>
              </div>
            </div>
            <div className="event-detail__actions">
              <Link to={`/events/${eventId}/edit`}>
                <Button>Edit Event</Button>
              </Link>
              <Button variant="danger" onClick={() => setDeleteModalOpen(true)}>
                Delete
              </Button>
            </div>
          </div>
        </Card>

        <div className="event-detail__stats">
          <Card>
            <div className="stat">
              <div className="stat__value">{invites.length}</div>
              <div className="stat__label">Total Invites</div>
            </div>
          </Card>
          <Card>
            <div className="stat">
              <div className="stat__value">{totalGuests}</div>
              <div className="stat__label">Total Guests</div>
            </div>
          </Card>
          <Card>
            <div className="stat">
              <div className="stat__value">{rsvpYes}</div>
              <div className="stat__label">Attending</div>
            </div>
          </Card>
          <Card>
            <div className="stat">
              <div className="stat__value">{rsvpNo}</div>
              <div className="stat__label">Declined</div>
            </div>
          </Card>
          <Card>
            <div className="stat">
              <div className="stat__value">{noResponse}</div>
              <div className="stat__label">No Response</div>
            </div>
          </Card>
        </div>

        <Card>
          <div className="invites-header">
            <button onClick={(e) => { e.preventDefault(); toggleAllInvitesExpanded(); }} className="invites-toggle-button">
              <h2>
                Invites
                <span className="expand-icon">
                  {expandedInvites.size === invites.length ? '▼' : '▶'}
                </span>
              </h2>
            </button>
            <div className="invites-header__actions">
              <Button
                variant="outline"
                onClick={handleExportGuests}
                disabled={guestCsvRows.length === 0}
              >
                Export CSV
              </Button>
              <Link to={`/events/${eventId}/invites/new`}>
                <Button>Add Invite</Button>
              </Link>
            </div>
          </div>

          <div className="invites-filters">
            <div className="filter-group">
              <label htmlFor="sortBy">Sort by:</label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'title' | 'email' | 'dietary' | 'respondedAt')}
                className="filter-select"
              >
                <option value="title">Title (A-Z)</option>
                <option value="email">Email</option>
                <option value="dietary">Dietary</option>
                <option value="respondedAt">Date Responded</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filterEmail">Email:</label>
              <select
                id="filterEmail"
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value as 'all' | 'has' | 'none')}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="has">Has Email</option>
                <option value="none">No Email</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filterRsvp">Guest RSVP:</label>
              <select
                id="filterRsvp"
                value={filterRsvp}
                onChange={(e) => setFilterRsvp(e.target.value as 'all' | 'yes' | 'no' | 'pending' | 'responded')}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="responded">Responded</option>
                <option value="yes">Attending</option>
                <option value="no">Declined</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filterDietary">Dietary:</label>
              <select
                id="filterDietary"
                value={filterDietary}
                onChange={(e) => setFilterDietary(e.target.value as 'all' | 'has' | 'none')}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="has">Has Requirements</option>
                <option value="none">No Requirements</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filterOtherInfo">Other Info:</label>
              <select
                id="filterOtherInfo"
                value={filterOtherInfo}
                onChange={(e) => setFilterOtherInfo(e.target.value as 'all' | 'has' | 'none')}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="has">Has Info</option>
                <option value="none">No Info</option>
              </select>
            </div>
          </div>

          {filteredAndSortedInvites.length === 0 ? (
            <p className="text-center text-muted">
              {invites.length === 0 ? 'No invites yet. Add some invites!' : 'No invites match the current filters.'}
            </p>
          ) : (
            <div className="invites-list">
              {filteredAndSortedInvites.map((invite) => (
                <div key={invite.uuid} className="invite-item">
                  <div 
                    className="invite-header" 
                    onClick={() => toggleInviteExpanded(invite.uuid)}
                  >
                    <div className="invite-info">
                      <div className="invite-title-row">
                        <span className="expand-icon">
                          {expandedInvites.has(invite.uuid) ? '▼' : '▶'}
                        </span>
                        <h3 className="invite-title">{invite.title}</h3>
                        <span className="guest-count">({invite.guests.length} guest{invite.guests.length !== 1 ? 's' : ''})</span>
                      </div>
                      <div className="invite-shortcode">
                        Code: <strong>{invite.shortcode}</strong>
                      </div>
                      {invite.rsvpSubmittedAt !== null && (
                        <div className="invite-responded-at">
                          Responded: {new Date(invite.rsvpSubmittedAt).toLocaleString()}
                        </div>
                      )}
                      {invite.email && (
                        <div className="invite-email">
                          <span className="email-icon">✉</span> {invite.email}
                        </div>
                      )}
                      {invite.dietary && (
                        <div className="invite-dietary">
                          <span className="dietary-icon">🍽</span> {invite.dietary}
                        </div>
                      )}
                      {invite.songRequest && (
                        <div className="invite-song-request">
                          <span className="song-icon">♫</span> {invite.songRequest}
                        </div>
                      )}
                      {invite.otherInfo && (
                        <div className="invite-other-info">
                          <span className="info-icon">ℹ</span> {invite.otherInfo}
                        </div>
                      )}
                    </div>
                    <div className="invite-actions" onClick={(e) => e.stopPropagation()}>
                      <Link to={`/events/${eventId}/invites/${invite.uuid}/edit`}>
                        <Button size="small">Edit</Button>
                      </Link>
                      <Link to={`/events/${eventId}/invites/${invite.uuid}/guests/new`}>
                        <Button size="small">Add Guest</Button>
                      </Link>
                      <Button
                        size="small"
                        variant="danger"
                        onClick={() => handleDeleteInvite(invite.uuid)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>

                  {expandedInvites.has(invite.uuid) && (
                    <div className="guests-list">
                      {invite.guests.length === 0 ? (
                        <p className="text-muted no-guests">No guests added yet</p>
                      ) : (
                        invite.guests.map((guest) => (
                          <div key={guest.uuid} className="guest-item">
                            <div className="guest-info">
                              <div className="guest-name">{guest.name}</div>
                            </div>
                            <div className="guest-status">
                              {guest.rsvp === true && <span className="status status--yes">✓ Attending</span>}
                              {guest.rsvp === false && <span className="status status--no">✗ Not Attending</span>}
                              {(guest.rsvp === null || guest.rsvp === undefined) && <span className="status status--pending">? Pending</span>}
                            </div>
                            <div className="guest-actions">
                              <Link to={`/events/${eventId}/invites/${invite.uuid}/guests/${guest.uuid}/edit`}>
                                <Button size="small">Edit</Button>
                              </Link>
                              <Button
                                size="small"
                                variant="danger"
                                onClick={() => handleDeleteGuest(invite.uuid, guest.uuid)}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Event"
      >
        <p>Are you sure you want to delete this event? This action cannot be undone.</p>
        <div className="modal-actions">
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete Event'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default EventDetail;
